import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";

import { useMutationWithError } from "@/utils/hooks/useMutationWithError";
import { useFlags } from "@/utils/launchDarkly/useFlags";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import {
  AddPaymentRaincheckToBookingInput,
  useAddPaymentRaincheckToBookingMutation,
} from "./__generated__/useAddPaymentRaincheckToBookingMutation.graphql";

export const useAddPaymentRaincheckToBooking = () => {
  const { showToast } = useToast();
  const { patronLoyalty } = useFlags();
  const [addPaymentRaincheckToBooking, isAddingPaymentRaincheckToBooking] =
    useMutationWithError<useAddPaymentRaincheckToBookingMutation>(
      useAddPaymentRaincheckToBookingMutationString
    );

  const handleResult = (
    success: boolean | null | undefined,
    bookingId: string | null | undefined,
    successCallback: (bookingId: string) => void
  ) => {
    if (success) {
      successCallback(bookingId ?? "");
      showToast({
        description: t`Raincheck added to booking.`,
        semanticStyle: "success",
        toastId: "add-payment-raincheck-to-booking-success",
      });
    } else {
      showToast({
        description: t`Sorry. An error occurred while adding a raincheck to the booking.`,
        semanticStyle: "error",
        toastId: "add-payment-raincheck-to-booking-error",
      });
    }
  };

  const handleAddPaymentRaincheckToBooking = (
    input: AddPaymentRaincheckToBookingInput,
    successCallback: (bookingId: string) => void
  ) => {
    addPaymentRaincheckToBooking({
      variables: { input, patronLoyalty: !!patronLoyalty },
      onCompleted: (response) =>
        handleResult(
          response.responsePayload?.success,
          response.responsePayload?.booking?.id,
          successCallback
        ),
    });
  };

  return [
    handleAddPaymentRaincheckToBooking,
    isAddingPaymentRaincheckToBooking,
  ] as const;
};

export const useAddPaymentRaincheckToBookingMutationString = graphql`
  mutation useAddPaymentRaincheckToBookingMutation(
    $input: AddPaymentRaincheckToBookingInput!
    $patronLoyalty: Boolean!
  ) {
    responsePayload: addPaymentRaincheckToBooking(input: $input) {
      success
      errors {
        field
        code
        message
      }
      booking {
        id
        totalDueAfterCredits
        projectedCreditApplications @include(if: $patronLoyalty) {
          amount
          paymentSource
          squareGiftCard {
            id
            ganLastFour
          }
          raincheck {
            id
          }
        }
        ... on StandardBooking {
          paymentRainchecks {
            edges {
              node {
                id
              }
            }
          }
        }
        ... on LessonBooking {
          paymentRainchecks {
            edges {
              node {
                id
              }
            }
          }
        }
      }
    }
  }
`;
